import { lazy, ReactNode, Suspense } from 'react';
import { ConfigurationCanvasLeftPanel } from '../configuration-canvas-panel';
import { useSelector } from 'react-redux';
import { DocumentationContextProvider } from '../../documentation/context/DocumentationContext';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { selectDefaultCreationEnvironment } from '../environments/environmentsReducer';
import { GlobalRenderer } from '../global-renderer/GlobalRenderer';

const DocumentationDialog = lazy(() => import('../../documentation/DocumentationDialog'));
const SnowflakeDocumentationDialog = lazy(
  () => import('../../documentation/snowflake/SnowflakeDocumentationDialog'),
);

interface DocumentationPageProps {
  crmOrgId: string;
}

const DocumentationPageShell = ({ children }: { children: ReactNode }) => (
  <ConfigurationCanvasLeftPanel sx={{ pb: '24px' }} withExpandedModeButton>
    <Suspense fallback={<CenteredCircularProgress />}>{children}</Suspense>
  </ConfigurationCanvasLeftPanel>
);

const SalesforceDocumentationPage = ({ crmOrgId }: DocumentationPageProps) => (
  <DocumentationContextProvider crmOrgId={crmOrgId}>
    <DocumentationPageShell>
      <DocumentationDialog crmOrgId={crmOrgId} hideAddObjectButton={false} />
    </DocumentationPageShell>
  </DocumentationContextProvider>
);

const SnowflakeDocumentationPage = ({ crmOrgId }: DocumentationPageProps) => (
  <DocumentationPageShell>
    <SnowflakeDocumentationDialog crmOrgId={crmOrgId} />
  </DocumentationPageShell>
);

const DocumentationPage = () => {
  const defaultCreationEnv = useSelector(selectDefaultCreationEnvironment);
  const isSnowflake = defaultCreationEnv?.orgType === 'snowflake';
  const crmOrgId = defaultCreationEnv?.id ?? '';

  return (
    <GlobalRenderer>
      {isSnowflake ? (
        <SnowflakeDocumentationPage crmOrgId={crmOrgId} />
      ) : (
        <SalesforceDocumentationPage crmOrgId={crmOrgId} />
      )}
    </GlobalRenderer>
  );
};

export { DocumentationPage };
